/**
 * A collection read outside React, always FRESH, that leaves the screens' cache entry warm.
 *
 * A file built on a click (an export, a pack) must state what the platform holds NOW. A run's
 * report carries `disposition` and `decided_units`, which change the moment a planner decides a
 * line, so a file never reads the session's cached copy: `collectFresh` walks every page and only
 * AFTERWARDS writes the answer into the entry `useApiCollection` reads (`collectionKey`), so a
 * screen that needs only the names — immutable for the run — finds them without a walk of its own.
 *
 * With no client registered (a unit test, a script) it is `collectPages`, exactly as before.
 */

import type { QueryClient } from '@tanstack/react-query';

import type { OperationDescriptor, ParamsOf } from './generated/operations';
import { collectPages, type ItemOf } from './http';

/**
 * The cache policy for a read whose USE cannot change under its key: fresh for the whole session,
 * kept an hour after its last reader leaves. Spread into the options of ONE call, never into a
 * default. The use matters, not the collection: a run's report names its stores and articles for
 * good, but its rows also carry `disposition` and `decided_units`, which move with every decision —
 * so a screen may read the NAMES from this entry, and a file never reads it (`collectFresh`).
 */
export const SESSION_LONG_READ = { staleTime: Infinity, gcTime: 60 * 60_000 } as const;

let registered: QueryClient | null = null;

/** The app's one `QueryClient`, told once at start (`App.tsx`); `null` forgets it (tests). */
export function registerQueryClient(client: QueryClient | null): void {
  registered = client;
}

/** The app's registered `QueryClient`, or `null` outside the app (a unit test, a script). */
export function registeredQueryClient(): QueryClient | null {
  return registered;
}

/** The cache key of one collection: what `useApiCollection` reads and `collectFresh` refreshes. */
export function collectionKey<S extends OperationDescriptor>(
  operation: S, params: ParamsOf<S>, language: string,
): readonly unknown[] {
  return [operation.operationId, 'all', params, language];
}

/** Every page of a collection, walked now; the screens' cache entry is refreshed with the answer. */
export async function collectFresh<S extends OperationDescriptor>(
  operation: S, params: ParamsOf<S>, language: string,
): Promise<readonly ItemOf<S>[]> {
  const items = await (collectPages(operation, params, { language }) as Promise<readonly ItemOf<S>[]>);
  registered?.setQueryData(collectionKey(operation, params, language), items);
  return items;
}
